import { IonButton, IonIcon } from '@ionic/react';
import React, { useMemo } from 'react';

import ButtonContainer from '@/components/common/buttonContainer/ButtonContainer';
import Spacer from '@/components/common/spacer/Spacer';
import Selection from '@/components/selection/Selection';
import ScheduleFlowSummary from '@/components/summary/scheduleFlowSummary/ScheduleFlowSummary';
import { useLanguage } from '@/contexts/LanguageContext';
import { useScheduleFlow } from '@/contexts/ScheduleFlowContext';
import { Dentist } from '@/data/Dentists';
import useToast from '@/hooks/useToast';
import { SCHEDULE_FLOW_STEPS } from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { isClinicName } from '@/utils/helpers/isClinicName';
import { arrowForwardCircleOutline, calendarOutline } from 'ionicons/icons';

import './SelectDentist.css';

const SelectDentist: React.FC = () => {
  const { t } = useLanguage();
  const displayToast = useToast();
  const {
    clinic,
    dentists,
    selectedDentist,
    setSelectedDentist,
    setCurrentStep,
    setPurpose,
    setAvailableDates,
    setSelectedDate,
    setSelectedTime,
    setAvailableTimeslots
  } = useScheduleFlow();

  if (!clinic) return null;

  const individualDentist = dentists.length === 1;
  const skipSelectDentist = individualDentist && selectedDentist;
  const isSelectedDentistUsingClinicName = isClinicName(
    `${selectedDentist?.first_name} ${selectedDentist?.last_name}`
  );

  const isAllDentistsUsingClinicName = dentists.every((dentist) =>
    isClinicName(`${dentist.first_name} ${dentist.last_name}`)
  );
  const isSomeDentistsUsingClinicName = dentists.some((dentist) =>
    isClinicName(`${dentist.first_name} ${dentist.last_name}`)
  );

  const copy = useMemo(() => {
    const selectionPromptKey = isAllDentistsUsingClinicName
      ? 'clinic'
      : isSomeDentistsUsingClinicName
        ? 'clinicOrDentist'
        : 'dentist';

    return {
      subHeader: t('schedule.selectDentist.subHeader'),
      header: clinic.name,
      message: skipSelectDentist
        ? t('schedule.selectDentist.messageContinue')
        : t('schedule.selectDentist.messageWithSelection', {
            selectionPrompt: t(
              `schedule.selectDentist.selectionPrompts.${selectionPromptKey}`
            )
          }),
      buttonText: skipSelectDentist
        ? t('schedule.selectDentist.buttonTextContinue')
        : t('schedule.selectDentist.buttonTextNext')
    };
  }, [
    t,
    clinic.name,
    skipSelectDentist,
    isAllDentistsUsingClinicName,
    isSomeDentistsUsingClinicName
  ]);

  const getLabel = (dentist: Dentist) => {
    return (
      <>
        {dentist.specialization ? (
          <div className="dentist">
            <div className="name">
              {formatDentistName({
                dentist,
                returnDentistFullName: true
              })}
            </div>
            <div className="specialization">
              {dentist.specialization && `${dentist.specialization}`}
            </div>
          </div>
        ) : (
          formatDentistName({
            dentist,
            returnDentistFullName: true
          })
        )}
      </>
    );
  };

  const isSelected = (dentist: Dentist) => dentist.id === selectedDentist?.id;

  const selectDentist = (dentist: Dentist | null) => {
    setSelectedDentist(dentist);

    if (!dentist || dentist.id !== selectedDentist?.id) {
      setPurpose(null);
      setAvailableDates([]);
      setSelectedDate(null);
      setAvailableTimeslots([]);
      setSelectedTime(null);
    }
  };

  const onNext = () => {
    if (selectedDentist) {
      setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_PURPOSE);
    } else {
      displayToast({
        message: t('schedule.selectDentist.errorMessage'),
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  };

  return (
    <div id="selectDentist">
      <IonIcon color="primary" icon={calendarOutline} id="selectDentistIcon" />
      <h4 id="subHeader">{copy.subHeader}</h4>
      <h1 id="header">{copy.header}</h1>
      <p id="message">{copy.message}</p>
      <Spacer top={24} bottom={102}>
        {skipSelectDentist ? (
          <ScheduleFlowSummary
            dentist={isSelectedDentistUsingClinicName ? null : selectedDentist}
            showEstimate={isSelectedDentistUsingClinicName}
          />
        ) : (
          <Selection
            options={dentists}
            getLabel={getLabel}
            onClick={selectDentist}
            isSelected={isSelected}
          />
        )}
      </Spacer>
      <ButtonContainer>
        <IonButton
          id="nextButton"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong={true}
        >
          <IonIcon slot="start" icon={arrowForwardCircleOutline}></IonIcon>
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default SelectDentist;
